import { Schema } from "effect";
import { OutcomeSpec } from "./outcome-schema.ts";

// ---------------------------------------------------------------------------
// The v3.2 loop document schema.
//
// The document is decoded from a parsed JSON object (a loop document lives in
// the DB as `jsonb`, model §9). Effect `Schema` gives it types and error
// messages; `JSON.parse` (or an imported `.json` fixture) produces the plain
// object it decodes.
//
// The schema is deliberately permissive on the opaque parts of the surface —
// expressions are opaque `${…}` strings at parse time (root checking is the
// validator's job, model §10), and `harness` / `inputs` values are never read
// by the interpreter. Decode ignores excess keys by default, so a `$comment`
// carried over from the source YAML's annotations is accepted on any object
// and dropped from the decoded value.
// ---------------------------------------------------------------------------

/** An opaque expression string at parse time (`${…}`); the validator checks roots. */
const Expr = Schema.String;

/** The three executors a leaf loop or evaluator runs under (model §1). */
export const Executor = Schema.Literal("agent", "human", "deterministic");

/** A `bounds.on` value: a single verdict/cause/`entries`, or an array of them. */
const StrOrStrArray = Schema.Union(Schema.String, Schema.Array(Schema.String));

/** A route target: a node name / `stay`, or `{ to, delay?, when? }`. */
const RouteObject = Schema.Struct({
  to: Schema.String,
  delay: Schema.optional(Schema.Unknown),
  when: Schema.optional(Schema.Unknown),
});
const RouteTargetSchema = Schema.Union(Schema.String, RouteObject);
const Routes = Schema.Record({ key: Schema.String, value: RouteTargetSchema });

/** One `bounds:` entry (model §3). `on` accepts a single value or an array. */
const Bound = Schema.Struct({
  on: Schema.optional(StrOrStrArray),
  max: Schema.optional(Schema.Unknown),
  scope: Schema.optional(Schema.String),
  key: Schema.optional(Schema.Unknown),
  pool: Schema.optional(Schema.String),
});
const Bounds = Schema.Union(Schema.Array(Bound), Bound);

/**
 * `await: { resource, event, key }` — a field on a node, not a node type
 * (model §4). `resource` is a *decode-time* convenience: `decodeLoopDocument`
 * folds `{ resource: "github", event: "pr.checks.completed" }` into the single
 * event string `github:pr.checks.completed` and drops `resource`, so a decoded
 * `LoopNode.await.event` is always a plain string and no interpreter code ever
 * branches on `resource`. The fold is deliberately ONE-WAY: a decoded document
 * does not round-trip back to the two-field form.
 */
const AwaitSpec = Schema.Struct({
  resource: Schema.optional(Schema.String),
  event: Schema.String,
  key: Schema.optional(Schema.Unknown),
  when: Schema.optional(Schema.Unknown),
});

/** One `timeout:` entry; `timeout` may be a single object or a list (model §4). */
const TimeoutSpec = Schema.Struct({
  after: Schema.optional(Schema.String),
  route: Schema.optional(Schema.String),
  when: Schema.optional(Schema.String),
  unless: Schema.optional(Schema.String),
  from: Schema.optional(Schema.String),
});
const Timeout = Schema.Union(Schema.Array(TimeoutSpec), TimeoutSpec);

/** `every: { after, route, max }` — a repeating timer on an awaiting node (model §4). */
const EverySpec = Schema.Struct({
  after: Schema.optional(Schema.String),
  route: Schema.optional(Schema.String),
  max: Schema.optional(Schema.Number),
});

/** A `for_each.source` standard-library loop reference (model §5). */
const LoopRef = Schema.Struct({
  loop: Schema.String,
  inputs: Schema.optional(Schema.Unknown),
  where: Schema.optional(Schema.Unknown),
});

/** One `on_vanish:` / `on_late:` rule; the block may also be a bare value. */
const WhenDoSpec = Schema.Struct({
  when: Schema.optional(Schema.Unknown),
  do: Schema.optional(Schema.String),
});
const WhenDo = Schema.Union(Schema.Array(WhenDoSpec), WhenDoSpec, Schema.String);

/** `close:` — a predicate expression, a duration literal, or an evaluator node name (model §5). */
const CloseSpec = Schema.Union(
  Schema.String,
  Schema.Struct({ after: Schema.optional(Schema.String) }),
);

/** The `for_each` block form (model §5); `for_each` may also be a bare expression. */
const ForEachBlock = Schema.Struct({
  source: Schema.optional(Schema.Union(Expr, LoopRef)),
  live: Schema.optional(Schema.Boolean),
  close: Schema.optional(CloseSpec),
  on_vanish: Schema.optional(WhenDo),
  on_late: Schema.optional(WhenDo),
});
const ForEach = Schema.Union(Schema.String, ForEachBlock);

/** `live:` as a sibling block, or a bare boolean. */
const LiveBlock = Schema.Struct({
  close: Schema.optional(CloseSpec),
  on_vanish: Schema.optional(WhenDo),
  on_late: Schema.optional(WhenDo),
});
const Live = Schema.Union(Schema.Boolean, LiveBlock);

/** `depends_on:` — edges plus the trust predicate (model §5). */
const DependsOn = Schema.Struct({
  edges: Schema.optional(Schema.Unknown),
  live: Schema.optional(Schema.Boolean),
  missing_blockers: Schema.optional(Schema.String),
  trust: Schema.optional(Schema.Unknown),
  on_untrusted: Schema.optional(Schema.String),
});

/** `exclusive_on:` — a list, a single spec, or a bare resource key (model §5). */
const ExclusiveSpec = Schema.Struct({
  resource: Schema.optional(Schema.String),
  key: Schema.optional(Schema.Unknown),
  scope: Schema.optional(Schema.String),
  when: Schema.optional(Schema.Unknown),
});
const ExclusiveOn = Schema.Union(Schema.Array(ExclusiveSpec), ExclusiveSpec, Schema.String);

/** `harness:` — opaque to the interpreter: a bare string or a record. */
const Harness = Schema.Union(Schema.String, Schema.Record({ key: Schema.String, value: Schema.Unknown }));

/**
 * `workspace:` — a provider record or the bare provider string (model §9).
 * The record keeps every other key it carries (`repo`, `base`, …): those are
 * the provider's own binding, resolved by the harness when it acquires the
 * workspace. A struct of `provider` alone silently dropped them on decode, so
 * the harness asked for a git workspace with no repo — and never got one.
 */
const WorkspaceObj = Schema.Struct(
  { provider: Schema.optional(Schema.String) },
  Schema.Record({ key: Schema.String, value: Schema.Unknown }),
);
const Workspace = Schema.Union(Schema.String, WorkspaceObj);

/** `compact:` — bounds path context by tokens or entries (model §9). */
const Compact = Schema.Struct({
  tokens: Schema.optional(Schema.Number),
  entries: Schema.optional(Schema.Number),
});

/** `evaluator: { executor }` — the evaluator node kind (model §1). */
const EvaluatorSpec = Schema.Struct({
  executor: Schema.optional(Executor),
});

/**
 * A node under a graph's `nodes` map. Every key is optional: a loop node keys
 * `loop:`, an evaluator keys `evaluator:`, a terminal is `{ outcome: true }`,
 * and a minimal node is one line. Unmodeled keys (`$comment`, …) are ignored.
 */
export const LoopNodeSchema = Schema.Struct({
  loop: Schema.optional(Schema.String),
  evaluator: Schema.optional(EvaluatorSpec),
  executor: Schema.optional(Executor),
  inputs: Schema.optional(Schema.Unknown),
  max_attempts: Schema.optional(Schema.Number),
  bounds: Schema.optional(Bounds),
  for_each: Schema.optional(ForEach),
  depends_on: Schema.optional(DependsOn),
  max_parallel: Schema.optional(Schema.Union(Schema.Number, Schema.String)),
  live: Schema.optional(Live),
  close: Schema.optional(CloseSpec),
  exclusive_on: Schema.optional(ExclusiveOn),
  await: Schema.optional(AwaitSpec),
  timeout: Schema.optional(Timeout),
  every: Schema.optional(EverySpec),
  on_interrupt: Schema.optional(Schema.String),
  on_cancel: Schema.optional(Schema.String),
  shadow: Schema.optional(Schema.String),
  compact: Schema.optional(Compact),
  routes: Schema.optional(Routes),
  prompt: Schema.optional(Schema.String),
  harness: Schema.optional(Harness),
  workspace: Schema.optional(Workspace),
  outcome: Schema.optional(Schema.Union(Schema.Boolean, OutcomeSpec)),
  head: Schema.optional(Schema.Array(Schema.String)),
  test: Schema.optional(Schema.String),
  on_vanish: Schema.optional(WhenDo),
  on_late: Schema.optional(WhenDo),
  version: Schema.optional(Schema.Number),
});

/** One declared input at the call site (model §2). */
const InputDecl = Schema.Struct({
  name: Schema.String,
  type: Schema.optional(Schema.Unknown),
  /** The declared JSON Schema (subset) of the input — what `outcome_contract` checks a binding against. */
  schema: Schema.optional(Schema.Unknown),
  required: Schema.optional(Schema.Boolean),
  default: Schema.optional(Schema.Unknown),
});

/** A trigger `on:` — the `{ kind, key, when }` object, or a bare `event` / `cron …` string (model §7). */
const TriggerOn = Schema.Union(
  Schema.String,
  Schema.Struct({
    kind: Schema.optional(Schema.String),
    key: Schema.optional(Schema.Unknown),
    when: Schema.optional(Schema.Unknown),
    event: Schema.optional(Schema.String),
    cron: Schema.optional(Schema.String),
    manual: Schema.optional(Schema.Unknown),
  }),
);
const TriggerSpec = Schema.Struct({
  on: Schema.optional(TriggerOn),
  key: Schema.optional(Schema.Unknown),
  when: Schema.optional(Schema.Unknown),
  concurrency: Schema.optional(Schema.String),
  inputs: Schema.optional(Schema.Unknown),
  supersedes: Schema.optional(Schema.Unknown),
});
const Trigger = Schema.Union(Schema.Array(TriggerSpec), TriggerSpec);

/** A `pools:` entry — a shared counter declared at graph level (model §3). */
const PoolSpec = Schema.Struct({
  max: Schema.optional(Schema.Unknown),
  scope: Schema.optional(Schema.String),
});
const Pools = Schema.Record({ key: Schema.String, value: PoolSpec });

/**
 * The `resources:` map — the document's binding of a resource *instance* to its
 * inputs (`resources: { github: { repo: "${inputs.repo}" }, linear: {} }`). The
 * key is the resource name an `await.resource` names; the value is an opaque
 * per-resource config block. The registry-time catalog check (does `github`
 * exist, does it declare `pr.checks.completed`?) lives in the api; the
 * validator here only knows the names this document declares.
 */
const Resources = Schema.Record({
  key: Schema.String,
  value: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
});

/** A `watch:` entry — a graph-level evaluator on a cadence (model §4). */
const WatchSpec = Schema.Struct({
  name: Schema.optional(Schema.String),
  when: Schema.optional(Schema.Unknown),
  route: Schema.optional(Schema.String),
  await: Schema.optional(AwaitSpec),
});

/**
 * A complete v3.2 loop document. One permissive struct covers both a graph
 * (keys `nodes` / `trigger` / `pools` / `watch`) and a leaf loop (keys `head` /
 * `executor` / `harness`); the two share `loop` / `version` / `outcome` (prose)
 * / `workspace` / `inputs`.
 */
export const LoopDocumentSchema = Schema.Struct({
  loop: Schema.String,
  version: Schema.optional(Schema.Number),
  outcome: Schema.optional(Schema.Union(Schema.String, OutcomeSpec)),
  head: Schema.optional(Schema.Array(Schema.String)),
  /** Named schemas this document publishes; a `$ref` reaches them as `#/schemas/<name>`. */
  schemas: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  executor: Schema.optional(Executor),
  workspace: Schema.optional(Workspace),
  inputs: Schema.optional(Schema.Array(InputDecl)),
  harness: Schema.optional(Harness),
  pools: Schema.optional(Pools),
  resources: Schema.optional(Resources),
  trigger: Schema.optional(Trigger),
  nodes: Schema.optional(Schema.Record({ key: Schema.String, value: LoopNodeSchema })),
  watch: Schema.optional(Schema.Array(WatchSpec)),
});

/** The decoded v3.2 document. */
export type LoopDocument = typeof LoopDocumentSchema.Type;

/** One decoded node under `document.nodes`. */
export type LoopNode = typeof LoopNodeSchema.Type;

/**
 * Whether a node ends the walk — the ONE sink predicate the validator, the graph
 * compiler, both interpreters and the cluster harness share. A sink is
 * `{ outcome: true }`, or — since v3.4 — a typed sink that declares what its
 * outcome IS (`outcome: { head, schema }`) and therefore neither calls a loop
 * nor routes.
 */
export const isSinkNode = (node: LoopNode): boolean =>
  node.outcome === true || (typeof node.outcome === "object" && node.loop === undefined && node.routes === undefined);

/**
 * Recursively drop `$comment` annotations before decoding. A `$comment` carried
 * over from the source YAML can sit as a sibling key inside a map that the
 * schema models as a `Schema.Record` (a `pools` / `routes` map), where excess-
 * key stripping does not reach it; removing it up front lets a parsed fixture
 * decode while keeping the annotation in the on-disk JSON.
 */
const stripComments = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripComments);
  }
  if (typeof value === "object" && value !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      if (key !== "$comment") {
        result[key] = stripComments(child);
      }
    }
    return result;
  }
  return value;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Fold one `await:`-carrying owner (a node, or a `watch:` entry) from the
 * two-field `{ resource, event }` form into the single event string the
 * interpreter already awaits: `<resource>:<event>`. An owner with no `await`,
 * no `resource`, or a non-string `event` is handed back untouched — the schema
 * decode that follows reports whatever is wrong with it.
 */
const foldAwaitOwner = (owner: unknown): unknown => {
  if (!isRecord(owner) || !isRecord(owner.await)) {
    return owner;
  }
  const { resource, ...rest } = owner.await;
  return typeof resource === "string" && typeof rest.event === "string"
    ? { ...owner, await: { ...rest, event: `${resource}:${rest.event}` } }
    : owner;
};

/** Apply {@link foldAwaitOwner} to every node and every `watch:` entry of a parsed document. */
const foldAwaitResources = (value: unknown): unknown => {
  if (!isRecord(value)) {
    return value;
  }
  const folded: Record<string, unknown> = { ...value };
  if (isRecord(value.nodes)) {
    folded.nodes = Object.fromEntries(
      Object.entries(value.nodes).map(([id, node]) => [id, foldAwaitOwner(node)]),
    );
  }
  if (Array.isArray(value.watch)) {
    folded.watch = value.watch.map(foldAwaitOwner);
  }
  return folded;
};

/**
 * Decode an unknown (parsed-JSON) value into a `LoopDocument`, ignoring
 * `$comment` annotations and folding every `await.resource` into its `event`.
 */
export const decodeLoopDocument = (input: unknown) =>
  Schema.decodeUnknown(LoopDocumentSchema)(foldAwaitResources(stripComments(input)));

/**
 * The fixed expression roots of model v3.2 §10 — exactly twelve. A root may
 * name what the interpreter, the harness or the workspace provider knows; it
 * may never name a vendor. Exported for the validator; the schema does not
 * validate against it (an expression is opaque at parse time).
 */
export const EXPRESSION_ROOTS = [
  "inputs",
  "nodes.<name>.output",
  "nodes.<name>.outcome",
  "context",
  "item",
  "external",
  "attempt",
  "placement",
  "workspace",
  "harness",
  "previous",
  "pools",
] as const;

/**
 * The distinct leading identifiers of {@link EXPRESSION_ROOTS} — the token
 * before the first `.` of each root. `nodes.<name>.output` and
 * `nodes.<name>.outcome` collapse to the single leading root `nodes` (and
 * `nodes.<fan-out>.collection` reads under it, model v3.2 §5). This is what a
 * leading-root check compares an expression's first segment against.
 */
export const EXPRESSION_ROOT_HEADS = [
  ...new Set(EXPRESSION_ROOTS.map((root) => root.split(".")[0])),
] as const;
